// src/components/common/DateWithHint.jsx
import React from 'react';
import { Box, Typography } from '@mui/material';
import { format, differenceInCalendarDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { parseUTCToLocalTimezone, getDateLocale } from '../../i18n/LocalizedDateDistance';

/**
 * A utility component for displaying dates with contextual hints (days only)
 * @param {Object} props
 * @param {string|Date} props.date - The date to display
 * @param {string} props.label - The label to display above the date (e.g., "Scheduled", "Due")
 * @param {string} [props.dateFormat="d.M.yyyy"] - Date format string for date-fns
 * @param {boolean} [props.showHint=true] - Whether to show the contextual hint
 * @param {string} [props.hintType="auto"] - Type of hint: "auto", "countdown", "none"
 * @param {boolean} [props.highlightOverdue=true] - Whether to highlight overdue dates in red
 * @param {boolean} [props.highlightUpcoming=true] - Whether to highlight upcoming dates
 * @param {number} [props.upcomingThreshold=7] - Days threshold for "upcoming" styling
 * @param {Object} [props.sx] - Additional Material-UI sx styles
 */
export default function DateWithHint({
  date,
  label,
  dateFormat = 'd.M.yyyy',
  showHint = true,
  showDate = true,
  hintType = 'auto',
  highlightOverdue = true,
  highlightUpcoming = true,
  upcomingThreshold = 7,
  sx = {},
}) {
  const { t, i18n } = useTranslation();

  if (!date) {
    return null;
  }

  // Parse date and convert to local timezone
  const dateObj = typeof date === 'string' 
    ? parseUTCToLocalTimezone(date, i18n.language)
    : date;
  
  const now = new Date();
  
  // Use calendar days difference to ignore time components
  const daysFromNow = differenceInCalendarDays(dateObj, now);
  const isOverdue = daysFromNow < 0;
  const isUpcoming = daysFromNow <= upcomingThreshold && daysFromNow > 0;
  const isToday = daysFromNow === 0;

  // Generate hint text based on hintType
  const getHintText = () => {
    if (!showHint || hintType === 'none') return '';

    switch (hintType) {
      case 'countdown':
      case 'auto':
      default:
        if (isToday) return t('common:dateHints.today');
        if (daysFromNow === 1) return t('common:dateHints.tomorrow');
        if (daysFromNow === -1) return t('common:dateHints.yesterday');
        if (daysFromNow > 0) {
          return t('common:dateHints.inDays', { count: daysFromNow });
        }
        if (daysFromNow < 0) {
          const daysAgo = Math.abs(daysFromNow);
          return t('common:dateHints.daysAgo', { count: daysAgo });
        }
        return '';
    }
  };

  const hintText = getHintText();

  // Date always uses optimal contrast (black/dark text)
  const getDateColor = () => {
    return 'text.primary'; // Always use high contrast text
  };

  const getHintColor = () => {
    if (highlightOverdue && isOverdue) return 'error.main';
    if (highlightUpcoming && isUpcoming) return 'warning.main';
    if (isToday) return 'info.main';
    return 'text.disabled';
  };

  return (
    <Box sx={sx}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {showDate && (
        <Typography 
          variant="body2" 
          fontWeight="medium"
          color={getDateColor()}
        >
          {format(dateObj, dateFormat, { locale: getDateLocale(i18n.language) })}
        </Typography>
      )}
      {hintText && (
        <Typography 
          variant="caption" 
          color={getHintColor()}
          display="block"
          sx={{ mt: -0.25 }}
        >
          {hintText}
        </Typography>
      )}
    </Box>
  );
}

// Export additional utility functions that might be useful
export const dateHintUtils = {
  /**
   * Get a formatted date with hint for display (days only)
   */
  getDateWithHint: (date, label, options = {}, t, language) => {
    const dateObj = typeof date === 'string' 
      ? parseUTCToLocalTimezone(date, language)
      : date;
    const formattedDate = format(dateObj, options.dateFormat || 'd.M.yyyy', { 
      locale: getDateLocale(language) 
    });
    const daysFromNow = differenceInCalendarDays(dateObj, new Date());
    
    let hint = '';
    if (options.showHint !== false && t) {
      if (daysFromNow === 0) hint = t('common:dateHints.today');
      else if (daysFromNow === 1) hint = t('common:dateHints.tomorrow');
      else if (daysFromNow === -1) hint = t('common:dateHints.yesterday');
      else if (daysFromNow > 0) {
        hint = t('common:dateHints.inDays', { count: daysFromNow });
      }
      else if (daysFromNow < 0) {
        const daysAgo = Math.abs(daysFromNow);
        hint = t('common:dateHints.daysAgo', { count: daysAgo });
      }
    }
    
    return { formattedDate, hint, label };
  },

  /**
   * Get the status of a date (overdue, upcoming, today, etc.)
   */
  getDateStatus: (date, upcomingThreshold = 7, language) => {
    const dateObj = typeof date === 'string' 
      ? parseUTCToLocalTimezone(date, language)
      : date;
    const daysFromNow = differenceInCalendarDays(dateObj, new Date());
    
    if (daysFromNow === 0) return 'today';
    if (daysFromNow < 0) return 'overdue';
    if (daysFromNow <= upcomingThreshold) return 'upcoming';
    return 'future';
  },

  /**
   * Check if a date is overdue (days only)
   */
  isOverdue: (date, language) => {
    const dateObj = typeof date === 'string' 
      ? parseUTCToLocalTimezone(date, language)
      : date;
    return differenceInCalendarDays(dateObj, new Date()) < 0;
  },

  /**
   * Get days difference (positive = future, negative = past, 0 = today)
   */
  getDaysFromNow: (date, language) => {
    const dateObj = typeof date === 'string' 
      ? parseUTCToLocalTimezone(date, language)
      : date;
    return differenceInCalendarDays(dateObj, new Date());
  }
};